import { CaretDownIcon, CaretRightIcon } from '@panwds/icons';
import { classNames, twMerge } from '@panwds/tailwindcss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { metricsHelper, pathMatch } from '../../utils/index.js';
import { FlyOutMenu } from '../FlyOutMenu/index.js';
import { useNavigationContext } from '../NavigationContext/index.js';
export function SubNavList({ dataMetrics, dataTestId, id, isSimpleNav, isProductDivider, hasDivider, lastParent, icon, title, url, children, addClassName, active, disabled, onOpen, onHover, hoveredItem, openedItems, openedItem, groupId = undefined, defaultOpen = false }) {
    const hasArrow = !!children;
    const { collapsed } = useNavigationContext();
    const hoverTimeOut = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const flyOutRef = useRef(null);
    const location = useLocation();
    const history = useHistory();
    // When not wrapped in AccordionWrapper track the Open/Close state locally
    const [isOpen, setIsOpen] = useState(location.pathname.includes(url) ? true : defaultOpen);
    // Match is used only for Simple Nav to detect a click on the child item and style active accordion appropriately
    const match = !disabled && (active ?? (location.pathname.startsWith(`${url}`) && location.pathname !== url));
    const nonParentMatch = !disabled && (active ?? pathMatch(location.pathname, url));
    // If the component is wrapped in an AccordionWrapper the state is managed in AccordionWrapper else manage locally
    const currentOpen = onOpen && groupId !== undefined
        ? openedItems
            ? openedItems?.includes(groupId) ?? false
            : openedItem === groupId
        : isOpen;
    // When the component is loading and if there were an open nav before, set the current state to open
    useEffect(() => {
        if (onOpen) {
            if (location.pathname.includes(url)) {
                onOpen(groupId);
            }
        }
    }, []);
    useEffect(() => {
        if (isSimpleNav && onOpen) {
            if (location.pathname.includes(url)) {
                if (openedItems) {
                    !openedItems.includes(groupId) && onOpen(groupId);
                }
                else if (openedItem !== groupId) {
                    onOpen(groupId);
                }
            }
        }
    }, [location.pathname.includes(url)]);
    useEffect(() => {
        return () => {
            if (hoverTimeOut.current) {
                clearTimeout(hoverTimeOut.current);
            }
        };
    }, []);
    // Check if it is simple nav and accordion(s) is/are open and if the nav is collapsed hide the accordion <ul>
    const isHiddenOnCollapse = !currentOpen || (isSimpleNav && collapsed);
    const onMouseOverAction = () => {
        if (hoverTimeOut.current) {
            clearTimeout(hoverTimeOut.current);
        }
        onHover?.(groupId);
        setIsHovered(true);
    };
    const onMouseOutAction = () => {
        const timeOut = 1000;
        hoverTimeOut.current = setTimeout(() => {
            setIsHovered(false);
        }, timeOut);
    };
    const onClickAction = () => {
        if (children) {
            if (onOpen) {
                onOpen(groupId);
            }
            else {
                setIsOpen((prevState) => !prevState);
            }
        }
        else {
            history.push(url);
        }
    };
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case ' ':
            case 'Enter':
                e.preventDefault();
                collapsed && onMouseOverAction();
                onClickAction();
                break;
            case 'Escape':
                e.preventDefault();
                collapsed && setIsHovered(false);
                onClickAction();
                break;
        }
    }, [setIsOpen]);
    const classes = twMerge('twds-flex twds-box-border twds-m-0 twds-items-center twds-relative twds-pl-2 twds-pr-2 twds-py-2 twds-cursor-pointer hover:twds-bg-blue-steel-800 dark:hover:twds-bg-gray-800 active:twds-bg-blue-steel-600 dark:active:twds-bg-gray-700 hover:twds-text-white dark:hover:twds-text-white focus:twds-outline-none focus-visible:twds-bg-blue-steel-800 dark:focus:twds-bg-gray-800 focus:twds-text-white dark:focus:twds-text-white', !match && 'twds-text-blue-steel-300 dark:twds-text-gray-400', lastParent ? ' twds-text-xs twds-pl-4' : 'twds-text-sm', isProductDivider && 'twds-text-xs twds-uppercase twds-leading-4.5', !isSimpleNav && currentOpen && 'twds-border-b-0 twds-text-white dark:twds-text-white twds-font-semibold', isSimpleNav && match
        ? `${collapsed
            ? 'twds-border-blue-400 twds-bg-blue-steel-800 dark:twds-bg-gray-950'
            : 'twds-bg-blue-steel-900 dark:twds-bg-black'} twds-text-white dark:twds-text-white`
        : `${!children && nonParentMatch
            ? 'twds-bg-blue-steel-700 dark:twds-bg-gray-950 twds-text-white twds-border-l-blue-400 dark:twds-border-l-blue-400'
            : 'twds-bg-blue-steel-900 dark:twds-bg-black twds-border-blue-steel-900 dark:twds-border-black hover:twds-border-blue-steel-800 dark:hover:twds-border-gray-800 active:twds-border-blue-steel-600 dark:active:twds-border-gray-700'}`, isSimpleNav
        ? `twds-h-10 twds-pl-5 twds-m-0 twds-border-l-4 ${!collapsed && ' twds-border-transparent'} twds-border-solid twds-border-t-0 twds-border-b-0 twds-border-r-0`
        : `twds-h-8 twds-ml-2 twds-border-solid twds-border-b-0 twds-border-t-0 twds-border-r-0 ${!children ? 'twds-border-l-0 twds-border-l-transparent dark:twds-border-l-transparent' : 'twds-border-l-0'} ${nonParentMatch && !children
            ? 'twds-bg-blue-steel-700 dark:twds-bg-gray-950'
            : 'twds-bg-transparent dark:twds-bg-transparent'}`, !isSimpleNav && match && 'twds-text-blue-steel-300 dark:twds-text-gray-400', hasDivider
        ? `${!children && nonParentMatch ? 'twds-border-0' : 'twds-border-t-[1px] twds-border-[#354252]'}`
        : 'twds-border-t-0', addClassName);
    const ulClasses = classNames('twds-overflow-auto twds-scrollbar-small twds-scrollbar-no-track dark:twds-scrollbar-thumb-dark dark:twds-scrollbar-corner-dark twds-pb-1 twds-box-border twds-list-none twds-m-0 twds-p-0 twds-b-0', isHiddenOnCollapse ? 'twds-hidden' : '');
    const normalizedId = metricsHelper(id);
    return (React.createElement("li", { className: "twds-flex twds-flex-col twds-min-h-0" },
        React.createElement("button", { id: `navigation-${normalizedId}`, "aria-expanded": currentOpen, "aria-controls": `navigation-item-${normalizedId}`, "data-metrics": dataMetrics || `navigation-${normalizedId}`, "data-testid": dataTestId || `navigation-${id}`, "data-url": url, className: classes, onMouseOver: onMouseOverAction, onMouseOut: onMouseOutAction, onKeyDown: handleKeyDown, ref: flyOutRef, onClick: onClickAction },
            React.createElement("div", { className: `twds-flex twds-items-center ${icon && 'twds-mr-6'}` }, icon),
            hasArrow && isSimpleNav && (React.createElement(React.Fragment, null,
                React.createElement(CaretRightIcon, { className: classNames('twds-absolute twds-left-11 twds-transition-opacity twds-duration-150', collapsed ? 'twds-opacity-100' : 'twds-opacity-0'), size: 'xxs' }))),
            React.createElement("span", { className: `twds-truncate ${children ? 'twds-pr-6' : 'twds-pr-3'}` }, title),
            hasArrow && ((!collapsed && isSimpleNav) || !isSimpleNav) && (React.createElement(CaretDownIcon, { className: classNames('twds-absolute twds-right-0 twds-mr-4 twds-transition-transform', currentOpen ? 'twds-rotate-180' : 'twds-rotate-0'), size: 'xxs' }))),
        children && (React.createElement("ul", { id: `navigation-item-${normalizedId}`, "aria-labelledby": `navigation-${normalizedId}`, className: ulClasses }, children)),
        collapsed && isSimpleNav && children && (React.createElement(React.Fragment, null, isHovered && groupId === hoveredItem && (React.createElement(FlyOutMenu, { isSimpleNav: isSimpleNav, title: title, menuRef: flyOutRef, onMouseOver: onMouseOverAction, onMouseOut: onMouseOutAction }, children))))));
}
SubNavList.displayName = 'SubNavList';
//# sourceMappingURL=SubNavList.js.map